import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWishlist } from '~/features/commerce-api/hooks/wishlist/useWishList';
import { P80FavoritePageProps } from '~/features/favorites/favorites.definitions';
import { getSalesforceProductImages } from '~/lib/salesforce/utilities/product-images';
import { _transformAlgoliaToProductDetails } from '~/features/productList/Algoliga.utils';
import { useFavoriteProducts } from '~/features/productList/hooks/useFavoriteProducts';
import { mq } from '~/lib';
import { Modules } from '~/lib/data-contract';
import { Product } from '~/lib/data-contract/salesforce';
import { useProductHandler } from '~/services/search/useProductHandler';
import { Container } from '~/shared/components/Container';
import Flex from '~/shared/components/Flex';
import { NewProductCard } from '~/shared/components/NewProductCard';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { GTM_LIST_ID } from '~/shared/hooks/useEvents/useGTMEvents/models';
import { useUtils } from '~/shared/hooks/useUtils';
import { useTranslation } from '~/shared/utils/translation';
import { DynamicBlockList } from '~/templates/blocks';
import { breakpoints } from '~/theme/breakpoints';
import { SORT_OPTIONS, SortOption } from './components/constants';
import { ProductListSkeleton } from './components/ProductListSkeleton';
import { SortDropdown } from './components/SortDropdown';
import { useFrame } from '~/shared/utils';

const Wrapper = styled(Container)(({ theme }) => ({
    [mq(0, 'sm')]: {
        paddingInline: theme.space[3],
    },
}));

const StyledProductCardWrapper = styled.div(() => ({
    minWidth: 0,
    minHeight: '100%',
}));

const List = styled.div(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    columnGap: 8,
    rowGap: 28,
    paddingTop: theme.space[2],

    [breakpoints.sm]: {
        gridTemplateColumns: 'repeat(3, 1fr)',
        columnGap: 20,
        rowGap: 60,
        paddingBottom: theme.space[8],
    },
    [breakpoints.lg]: {
        gridTemplateColumns: 'repeat(4, 1fr)',
    },
}));

const StyledProductCard = styled(NewProductCard)(() => ({
    minHeight: '100%',
}));

export const StyledCount = styled.span<{ isVisible: boolean }>(({ theme, isVisible }) => ({
    color: theme.colors.black,
    fontSize: theme.fontSizes.xs,
    fontFamily: theme.fonts.roboto,
    fontWeight: theme.fontWeights.medium,
    visibility: isVisible ? 'visible' : 'hidden',
}));

const SkeletonCard = styled.div({
    width: '100%',
    height: '100%',
});

export const P80FavoritePage = ({ pageElements, bottomPageElements }: P80FavoritePageProps) => {
    const { items: wishlistItems, handleFavourite, isFavorite } = useWishlist();
    const { translate } = useTranslation();
    const { convertPriceToLocale } = useUtils();
    const getProductHandler = useProductHandler();
    const { data: favoritesData, isLoading, isFetching } = useFavoriteProducts();
    const [sortOption, setSortOption] = useState<SortOption>(SORT_OPTIONS.POPULAR);
    const queryClient = useQueryClient();
    const { data: frameData } = useFrame();

    //Remove favorite product that was remove by user from query data without calling the product API
    useEffect(() => {
        if (favoritesData) {
            if (!isLoading && wishlistItems.length < (favoritesData?.length ?? 0)) {
                queryClient.setQueryData(['favorites'], (prevData: Product[] = []) => {
                    return prevData.filter((f) =>
                        wishlistItems.some((item) => item.productId === f.id),
                    );
                });
            }
        }
    }, [favoritesData, isLoading, wishlistItems]);

    const handleFavouriteOptimistic = useCallback(
        async (product: TProductDetails) => {
            const productId = product.id;
            const isCurrentlyFavorite = isFavorite?.(productId);

            queryClient.setQueryData<Product[]>(['favorites'], (prev = []) => {
                if (isCurrentlyFavorite) {
                    return prev.filter((p) => p.id !== productId);
                } else {
                    return [...prev, product as Product];
                }
            });

            try {
                await handleFavourite(product);
            } catch (err) {
                queryClient.setQueryData<Product[]>(['favorites'], (prev = []) => {
                    if (isCurrentlyFavorite) {
                        return [...prev, product as Product];
                    } else {
                        return prev.filter((p) => p.id !== productId);
                    }
                });
            }
        },
        [handleFavourite, isFavorite, queryClient],
    );

    const convertItem = useCallback((jsonData: Product): TProductDetails => {
        const updatedData = { ...jsonData };

        Object.keys(updatedData).forEach((key) => {
            if (key.startsWith('c_')) {
                const newKey = key.replace('c_', 'cust_');
                updatedData[newKey] = updatedData[key];
                delete updatedData[key];
            }
        });

        delete updatedData['cust_deliveryDaysText'];

        if (updatedData.cust_pageURL) {
            updatedData.url = updatedData.cust_pageURL;
        }

        const transformedItem = _transformAlgoliaToProductDetails(
            updatedData,
            translate,
            convertPriceToLocale,
            frameData?.productSettings,
        );

        /*
         * Mapped field by field rather than passed whole: `updatedData` has been reshaped
         * above - keys renamed, one deleted, `url` reassigned - so its declared type no longer
         * describes it, and passing it as a product needed a cast through `unknown`. None of
         * the three fields the image matching reads are touched by that reshaping.
         *
         * TODO(IMC-1671): Change to getSalesforceProductImages(product) once product types have been aligned.
         */
        const images = getSalesforceProductImages({
            id: updatedData.id,
            imageGroups: updatedData.imageGroups,
            variationValues: updatedData.variationValues,
        })?.map((image) => ({ ...image, src: image.link }));

        const finalProduct = {
            ...transformedItem,
            images: images ?? [],
        };

        return finalProduct;
    }, []);

    const calcDiscountPercentage = (discountAmount: number, price: number | undefined) => {
        return (Number(discountAmount) * 100) / (price ?? 0);
    };

    const memoizedFavorites = useMemo(() => {
        if (!favoritesData) return [];

        return favoritesData.map(convertItem).sort((a, b) => {
            switch (sortOption) {
                case SORT_OPTIONS.POPULAR:
                    return (a.cust_popularity || 0) - (b.cust_popularity || 0);
                case SORT_OPTIONS.PRICE_ASC:
                    return (a.price || 0) - (b.price || 0);
                case SORT_OPTIONS.PRICE_DESC:
                    return (b.price || 0) - (a.price || 0);
                case SORT_OPTIONS.DISCOUNT_PERCENT_DESC:
                    return (
                        (calcDiscountPercentage(a.cust_effectivePrice, a.price) || 0) -
                        (calcDiscountPercentage(b.cust_effectivePrice, b.price) || 0)
                    );
                case SORT_OPTIONS.NEWEST_DESC:
                    return b.cust_isNew === a.cust_isNew ? 0 : b.cust_isNew ? 1 : -1;
                case SORT_OPTIONS.RATINGS_DESC:
                    return (b.cust_yotpoAverageRating || 0) - (a.cust_yotpoAverageRating || 0);
                default:
                    return 0;
            }
        });
    }, [favoritesData, sortOption, convertItem]);

    const showSkeleton = isLoading || isFetching;
    const isEmpty = !isLoading && (!favoritesData?.length || !wishlistItems.length);
    const handleSortChange = (newSortOption: SortOption) => {
        setSortOption(newSortOption);
    };

    return (
        <Wrapper hasGutter>
            <DynamicBlockList elements={pageElements as Modules[]} />

            <Flex alignItems="center" justifyContent="space-between">
                <StyledCount isVisible={!showSkeleton && memoizedFavorites.length > 0}>
                    {favoritesData?.length ?? 0} {translate('plp.products')}
                </StyledCount>

                {!isEmpty && (
                    <SortDropdown sortOption={sortOption} handleSortChange={handleSortChange} />
                )}
            </Flex>

            {!isEmpty && (
                <List>
                    {showSkeleton
                        ? Array.from({ length: 4 }).map((_, index) => (
                              <StyledProductCardWrapper key={`skeleton-${index}`}>
                                  <SkeletonCard>
                                      <ProductListSkeleton />
                                  </SkeletonCard>
                              </StyledProductCardWrapper>
                          ))
                        : memoizedFavorites.map((product, index) => (
                              <StyledProductCardWrapper key={product.id}>
                                  <StyledProductCard
                                      product={product}
                                      trackingListId={GTM_LIST_ID.PRODUCT_LIST}
                                      position={index}
                                      imageLoading="lazy"
                                      onButtonClick={getProductHandler}
                                      onFavourite={() => handleFavouriteOptimistic(product)}
                                      isFavourite={isFavorite?.(product.id)}
                                  />
                              </StyledProductCardWrapper>
                          ))}
                </List>
            )}

            <DynamicBlockList elements={bottomPageElements as Modules[]} />
        </Wrapper>
    );
};
